import { Link } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { useScrollAnimation } from '../../hooks/useScrollAnimation';

export default function Footer() {
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showPopup, setShowPopup] = useState(false);
  const [popupType, setPopupType] = useState<'success' | 'error'>('success');
  const { ref, isVisible } = useScrollAnimation<HTMLElement>({ threshold: 0.1 });

  // Popup automatisch nach 5 Sekunden schließen
  useEffect(() => {
    if (showPopup && popupType === 'success') {
      const timer = setTimeout(() => {
        setShowPopup(false);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [showPopup, popupType]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const formData = new URLSearchParams();
      formData.append('email', email);

      const response = await fetch('https://readdy.ai/api/form/d60ros950i17hner1dm0', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formData.toString(),
      });

      if (response.ok) {
        setPopupType('success');
        setShowPopup(true);
        setEmail('');
      } else {
        setPopupType('error');
        setShowPopup(true);
      }
    } catch (error) {
      setPopupType('error');
      setShowPopup(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      {/* Bestätigungs-Popup */}
      {showPopup && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          {/* Overlay */}
          <div 
            className="absolute inset-0 bg-black/50 backdrop-blur-sm animate-fade-in"
            onClick={() => setShowPopup(false)}
          />
          
          {/* Popup Content */}
          <div className="relative bg-white rounded-2xl shadow-2xl max-w-md w-full p-8 animate-scale-in">
            {/* Schließen-Button */}
            <button
              onClick={() => setShowPopup(false)}
              className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-600 transition-colors cursor-pointer hover:bg-gray-100 rounded-full"
              aria-label="Schließen"
            >
              <i className="ri-close-line text-xl"></i>
            </button>

            {popupType === 'success' ? (
              <>
                {/* Erfolgs-Icon */}
                <div className="w-20 h-20 mx-auto mb-6 bg-gradient-to-br from-teal to-teal-light rounded-full flex items-center justify-center animate-bounce-in">
                  <i className="ri-check-line text-4xl text-white"></i>
                </div>

                {/* Erfolgs-Text */}
                <h3 className="text-2xl font-bold text-navy text-center mb-3">
                  Willkommen im Newsletter!
                </h3>
                <p className="text-gray-600 text-center mb-6 leading-relaxed">
                  Vielen Dank für Ihre Anmeldung! Sie erhalten ab sofort praktische Tipps und Hinweise für den Alltag mit Ihrem Seniorhund.
                </p>

                {/* Pfotenabdrücke Dekoration */}
                <div className="flex justify-center gap-2 mb-6">
                  <i className="fa-solid fa-paw text-teal/30 text-lg"></i>
                  <i className="fa-solid fa-paw text-teal/50 text-xl"></i>
                  <i className="fa-solid fa-paw text-teal/30 text-lg"></i>
                </div>

                <button
                  onClick={() => setShowPopup(false)}
                  className="w-full py-3 bg-teal text-white rounded-full font-medium hover:bg-teal-light transition-all duration-300 cursor-pointer hover:scale-105 whitespace-nowrap"
                >
                  Verstanden
                </button>
              </>
            ) : (
              <>
                {/* Fehler-Icon */}
                <div className="w-20 h-20 mx-auto mb-6 bg-gradient-to-br from-red-400 to-red-500 rounded-full flex items-center justify-center">
                  <i className="ri-error-warning-line text-4xl text-white"></i>
                </div>

                {/* Fehler-Text */}
                <h3 className="text-2xl font-bold text-navy text-center mb-3">
                  Etwas ist schiefgelaufen
                </h3>
                <p className="text-gray-600 text-center mb-6 leading-relaxed">
                  Leider konnte Ihre Anmeldung nicht verarbeitet werden. Bitte versuchen Sie es später erneut.
                </p>

                <button
                  onClick={() => setShowPopup(false)}
                  className="w-full py-3 bg-navy text-white rounded-full font-medium hover:bg-navy/90 transition-all duration-300 cursor-pointer hover:scale-105 whitespace-nowrap"
                >
                  Erneut versuchen
                </button>
              </>
            )}
          </div>
        </div>
      )}

      <footer ref={ref} className="bg-navy text-white overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-20">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
            {/* Brand Column */}
            <div className={`scroll-animate-left ${isVisible ? 'visible' : ''}`}>
              <h3 className="text-lg font-bold text-white mb-4">Hund-im-Alter</h3>
              <p className="text-gray-300 leading-relaxed mb-4">
                Würdevolles Management im Alter. Bedürfnisorientierte Unterstützung für Hundebesitzer mit älteren Hunden.
              </p>
              {/* Social Media Buttons - Temporär ausgeblendet, später wieder aktivieren */}
            </div>

            {/* Navigation Column */}
            <div className={`scroll-animate ${isVisible ? 'visible' : ''} stagger-2`}>
              <h4 className="text-sm font-semibold uppercase tracking-wider text-gray-400 mb-4">
                Navigation
              </h4>
              <ul className="space-y-3">
                {[
                  { to: '/', label: 'Start' },
                  { to: '/beduerfnisse', label: 'Bedürfnisse' },
                  { to: '/ratgeber', label: 'Ratgeber' },
                  { to: '/ueber-hund-im-alter', label: 'Über Hund-im-Alter' },
                ].map((link) => (
                  <li key={link.to}>
                    <Link 
                      to={link.to} 
                      className="text-base text-gray-300 hover:text-teal transition-all duration-300 cursor-pointer inline-block hover:translate-x-1"
                    >
                      {link.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            {/* Newsletter Column */}
            <div className={`scroll-animate-right ${isVisible ? 'visible' : ''}`}>
              <h4 className="text-sm font-semibold uppercase tracking-wider text-gray-400 mb-4">
                Newsletter
              </h4>
              <p className="text-gray-300 text-base mb-4">
                Alltag mit Seniorhunden – praktische Hinweise
              </p>
              <form onSubmit={handleSubmit} className="flex gap-2" id="newsletter-form" data-readdy-form>
                <input
                  type="email"
                  name="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Ihre E-Mail"
                  className="flex-1 px-4 py-3 rounded-lg bg-white/10 text-white placeholder-gray-400 text-base focus:outline-none focus:ring-2 focus:ring-teal transition-all duration-300 focus:bg-white/20"
                  required
                />
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="px-6 py-3 bg-teal text-white rounded-full font-medium hover:bg-teal-light transition-all duration-300 whitespace-nowrap cursor-pointer disabled:opacity-50 hover:scale-105 hover:shadow-lg"
                >
                  {isSubmitting ? 'Wird gesendet...' : 'Anmelden'}
                </button>
              </form>
            </div>
          </div>

          {/* Bottom Bar */}
          <div className={`mt-16 pt-8 border-t border-gray-700 scroll-animate ${isVisible ? 'visible' : ''} stagger-4`}>
            <div className="flex flex-col md:flex-row justify-between items-center gap-4">
              <p className="text-sm text-gray-400">
                © 2026 Hund-im-Alter. Alle Rechte vorbehalten.
              </p>
              <div className="flex items-center gap-6 text-sm">
                <Link to="/datenschutz" className="text-gray-400 hover:text-teal transition-all duration-300 cursor-pointer hover:scale-105">
                  Datenschutz
                </Link>
                <span className="text-gray-600">|</span>
                <Link to="/impressum" className="text-gray-400 hover:text-teal transition-all duration-300 cursor-pointer hover:scale-105">
                  Impressum
                </Link>
              </div>
            </div>
          </div>
        </div>
      </footer>
    </>
  );
}
